import React, { useEffect, useRef, useState } from "react"

import { Box, Flex, Text, useStyleConfig } from "@chakra-ui/react"
import { AnimatePresence, motion } from "framer-motion"
import { useMaxWidth } from "hooks"
import { useLocation } from "react-router-dom"

import { H1 } from "components/common"
import { FullWidthFooter } from "components/FullWidthFooter"
import { Main, PageHeader, SiteHeader } from "components/LayoutComponents"
import { Nav } from "components/Nav"
import { SiteLogo } from "components/SiteLogo"

import { LoadingScreen } from "components/LoadingScreen"

const PageContent = ({ children }: { children: React.ReactNode }) => (
  <AnimatePresence mode="wait">
    <motion.div
      className="page-content"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0 } }}
      transition={{ duration: 0.2, delay: 0.1 }}
    >
      {children}
    </motion.div>
  </AnimatePresence>
)

export const Layout = (props: any) => {
  const { clientData, children, currentPage, layout, mainRef, navRef } = props

  const { isMaxTabletView } = useMaxWidth()
  const location = useLocation()
  const siteHeaderRef = useRef<HTMLDivElement>(null)

  /** Final Check, Confirmation page, Logout page use centered layout */
  const isCenteredLayout = layout.version === "centered"
  const isCenterAligned = isMaxTabletView || isCenteredLayout
  const centeredStyles = useStyleConfig("LayoutCentered")

  const baseStyles = {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    width: "full",
  }

  const styles = isCenteredLayout
    ? { ...baseStyles, ...centeredStyles }
    : baseStyles

  const [isClientDataLoaded, setIsClientDataLoaded] = useState(false)

  useEffect(() => {
    if (clientData) {
      setIsClientDataLoaded(true)
    }
  }, [clientData])

  return (
    <Box __css={styles}>
      <LoadingScreen show={!isClientDataLoaded} />

      {isClientDataLoaded && (
        <>
          <SiteHeader ref={siteHeaderRef}>
            {clientData && (
              <SiteLogo
                alt={clientData.meta.title}
                src={clientData.meta.logo}
              />
            )}
          </SiteHeader>

          <Flex
            as={isCenteredLayout ? motion.main : motion.div}
            alignItems={isCenterAligned ? undefined : "flex-start"}
            flex={1}
            flexDirection={isCenterAligned ? "column" : "row"}
            maxWidth="6xl"
            paddingX={{ md: 4 }}
          >
            {isCenteredLayout && (
              <PageContent key={location.pathname}>{children}</PageContent>
            )}

            {!isCenteredLayout && (
              <>
                <Nav
                  ref={navRef}
                  flex={isMaxTabletView ? undefined : "0 1 27%"}
                  pages={clientData?.pages ?? []}
                />

                <Main ref={mainRef} flex="1" overflow="auto">
                  <PageContent key={location.pathname}>
                    <PageHeader>
                      <H1>{currentPage.title}</H1>
                      {currentPage.description && (
                        <Text>{currentPage.description}</Text>
                      )}
                    </PageHeader>
                    {children}
                  </PageContent>
                </Main>
              </>
            )}
          </Flex>

          <FullWidthFooter clientData={clientData} />
        </>
      )}
    </Box>
  )
}
